import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { ToggleGroup, ToggleGroupItem } from '../ui/toggle-group';
import { ChefHat, Wine, Plus, Minus, Loader2 } from 'lucide-react';
import api from '../../lib/api';
import { toast } from 'sonner';

// Dialog per creare un prodotto "al volo" durante la presa dell'ordine.
// Prezzo IVA inclusa. Usato sia dal cameriere sia dalla cassa (admin/manager/cassiere).
export const CustomItemDialog = ({ open, onOpenChange, orderId, onAdded }) => {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [price, setPrice] = useState('');
  const [vat, setVat] = useState('10');
  const [qty, setQty] = useState(1);
  const [station, setStation] = useState('cucina');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const reset = () => { setName(''); setPrice(''); setVat('10'); setQty(1); setStation('cucina'); setNotes(''); };

  const submit = async () => {
    const p = parseFloat(String(price).replace(',', '.'));
    if (!name.trim()) { toast.error(t('customItem.errName')); return; }
    if (isNaN(p) || p < 0) { toast.error(t('customItem.errPrice')); return; }
    setSaving(true);
    try {
      const { data } = await api.post(`/orders/${orderId}/items/custom`, {
        name: name.trim(),
        price: p,
        vat_rate: Number(vat),
        qty: Number(qty) || 1,
        station,
        notes: notes.trim(),
      });
      onAdded?.(data);
      toast.success(t('customItem.added'));
      reset();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('customItem.addError'));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="sm:max-w-md" data-testid="custom-item-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Plus className="h-5 w-5" /> {t('customItem.title')}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-1">
          <div>
            <Label htmlFor="ci-name">{t('customItem.name')}</Label>
            <Input id="ci-name" value={name} onChange={(e) => setName(e.target.value)} placeholder={t('customItem.namePlaceholder')} className="mt-1.5" data-testid="custom-item-name" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="ci-price">{t('customItem.price')}</Label>
              <div className="relative mt-1.5">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">€</span>
                <Input id="ci-price" type="number" step="0.10" min="0" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="0,00" className="pl-7 font-mono" data-testid="custom-item-price" />
              </div>
            </div>
            <div>
              <Label>{t('customItem.vatIncl')}</Label>
              <Select value={vat} onValueChange={setVat}>
                <SelectTrigger className="mt-1.5" data-testid="custom-item-vat"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="4">4%</SelectItem>
                  <SelectItem value="10">10%</SelectItem>
                  <SelectItem value="22">22%</SelectItem>
                  <SelectItem value="0">{t('customItem.vatExempt')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label>{t('customItem.station')}</Label>
            <ToggleGroup type="single" value={station} onValueChange={(v) => v && setStation(v)} className="mt-1.5 grid grid-cols-2 gap-2" data-testid="custom-item-station">
              <ToggleGroupItem value="cucina" className="h-12 rounded-xl border border-border data-[state=on]:border-primary data-[state=on]:bg-primary/10 data-[state=on]:text-primary" data-testid="custom-item-station-cucina">
                <ChefHat className="h-4 w-4 mr-2" /> {t('customItem.kitchen')}
              </ToggleGroupItem>
              <ToggleGroupItem value="bar" className="h-12 rounded-xl border border-border data-[state=on]:border-primary data-[state=on]:bg-primary/10 data-[state=on]:text-primary" data-testid="custom-item-station-bar">
                <Wine className="h-4 w-4 mr-2" /> {t('customItem.bar')}
              </ToggleGroupItem>
            </ToggleGroup>
          </div>

          <div>
            <Label>{t('customItem.qty')}</Label>
            <div className="mt-1.5 flex items-center gap-3">
              <Button type="button" variant="outline" size="icon" className="rounded-xl" onClick={() => setQty((q) => Math.max(1, q - 1))} data-testid="custom-item-qty-minus"><Minus className="h-4 w-4" /></Button>
              <span className="w-10 text-center font-display text-lg" data-testid="custom-item-qty">{qty}</span>
              <Button type="button" variant="outline" size="icon" className="rounded-xl" onClick={() => setQty((q) => q + 1)} data-testid="custom-item-qty-plus"><Plus className="h-4 w-4" /></Button>
            </div>
          </div>

          <div>
            <Label htmlFor="ci-notes">{t('customItem.notes')}</Label>
            <Input id="ci-notes" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder={t('customItem.notesPlaceholder')} className="mt-1.5" data-testid="custom-item-notes" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => { reset(); onOpenChange(false); }} data-testid="custom-item-cancel">{t('common.cancel')}</Button>
          <Button onClick={submit} disabled={saving} data-testid="custom-item-submit">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />} {t('common.add')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
